import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Autocomplete, Box, Button, Chip,
  CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  MenuItem, Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import { v4API } from '../../Api';
import { useV4 } from './V4Layout';
import { errorMessage } from './common';
import { buildConfiguration, CONFIGURATION_FIELDS, createForm, LABELS } from './configurationFields';
import SecretEditor, { SECRET_PURPOSES, secretMetadata, secretSource } from './SecretEditor';

const PAGE_SIZE = 25;
const REFERENCE_CAP = 500;
const ADVANCED_FIELDS = {
  routes: ['legacyKey', 'legacyMethod', 'providerOptions'],
  tariffs: ['legacyMultiplier', 'roundingIncrementUnits', 'creditIncrementUnits', 'adjustmentMinUnits', 'adjustmentMaxUnits'],
};
const TITLES = { merchants: 'Мерчанты', processings: 'Процессинги', routes: 'Маршруты', tariffs: 'Тарифы' };
const ERROR_MESSAGES = {
  PROCESSING_HAS_ROUTES: 'У процессинга есть маршруты, включая отключённые. Сначала удалите их или выберите в них другой процессинг.',
  REVISION_CONFLICT: 'Запись уже изменена другим оператором. Закройте форму, обновите список и откройте запись заново. Ваши изменения не сохранены.',
  MERCHANT_ID_IMMUTABLE: 'ID существующего мерчанта менять нельзя.',
  ROUTE_DEPENDENCY_DISABLED: 'Перед включением маршрута включите его процессинг и тариф.',
  CURRENCY_PRECISION_CONFLICT: 'Точность валюты отличается от уже используемой. Проверьте тариф и маршруты.',
  INVALID_SECRET_REFERENCE: 'Выберите настроенный секрет подходящего назначения или создайте новый.',
  CALLBACK_URL_REQUIRED: 'Для включения этого процессинга требуется URL обратного вызова.',
  WEBHOOK_SECRET_REQUIRED: 'Для включения требуется ссылка на секрет вебхука.',
  INVALID_PROVIDER_OPTIONS: 'Параметры не поддерживаются этим провайдером. Проверьте дополнительные настройки.',
  INVALID_ROUTE_REFERENCES: 'Проверьте процессинг, тариф, валюту и точность маршрута.',
  UNSUPPORTED_METHOD: 'Метод или валюта не поддерживаются выбранным провайдером.',
};

function configurationError(error) {
  const response = error?.response?.data;
  const code = response?.error?.code || response?.code || error?.code;
  return ERROR_MESSAGES[code] || errorMessage(error) || 'Не удалось выполнить операцию. Повторите попытку.';
}

export async function loadConfigurationReferences(kind, isCurrent = () => true) {
  const items = [];
  const cursors = new Set();
  let cursor;
  while (isCurrent() && items.length < REFERENCE_CAP) {
    const page = await v4API.configList(kind, { cursor, limit: Math.min(100, REFERENCE_CAP - items.length) });
    if (!isCurrent()) return { items: [], truncated: false };
    if (!Array.isArray(page?.items)) throw new Error('Некорректный ответ справочника');
    items.push(...page.items.slice(0, REFERENCE_CAP - items.length));
    if (!page.nextCursor) return { items, truncated: false };
    if (cursors.has(page.nextCursor)) return { items, truncated: true };
    cursor = page.nextCursor;
    cursors.add(cursor);
  }
  return { items, truncated: isCurrent() };
}

function Capabilities({ capabilities }) {
  if (!capabilities) return null;
  const features = { poll: 'Опрос статуса', lookup: 'Поиск платежа', cancel: 'Отмена', confirm: 'Подтверждение', receipt: 'Чеки', video: 'Видео', resourceIsolation: 'Изоляция ресурсов', requiresReceipt: 'Чек обязателен' };
  return <Box>
    <Typography variant="body2">Возможности провайдера: методы {(capabilities.methods || []).join(', ') || 'не указаны'}{capabilities.customMethods ? ' (код задаётся вручную)' : ''}; валюты {(capabilities.currencies || []).join(', ') || 'не указаны'}.</Typography>
    <Stack direction="row" useFlexGap flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
      {Object.entries(features).filter(([key]) => capabilities[key]).map(([key, label]) => <Chip key={key} size="small" label={label} />)}
    </Stack>
  </Box>;
}

function ConfigurationEditor({ kind, original, saving, saveError, onSave, onClose }) {
  const { system } = useV4();
  const [form, setForm] = useState(() => createForm(kind, original));
  const [errors, setErrors] = useState({});
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [references, setReferences] = useState({ providers: [], processings: [], tariffs: [], merchants: [] });
  const [loading, setLoading] = useState(true);
  const [referenceError, setReferenceError] = useState('');
  const [warnings, setWarnings] = useState([]);
  const [retry, setRetry] = useState(0);
  const [legacySearch, setLegacySearch] = useState('');
  const [legacyItems, setLegacyItems] = useState([]);
  const [legacyLoading, setLegacyLoading] = useState(false);
  const [legacyError, setLegacyError] = useState('');
  const [legacyTruncated, setLegacyTruncated] = useState(false);
  const [legacyConfirmation, setLegacyConfirmation] = useState(null);
  const [telegramConfirmation, setTelegramConfirmation] = useState(null);
  const [secrets, setSecrets] = useState([]);
  const [secretField, setSecretField] = useState(null);

  useEffect(() => {
    let current = true;
    setLoading(true);
    setReferenceError('');
    const load = async () => {
      const result = { providers: [], processings: [], tariffs: [], merchants: [] };
      const notices = [];
      if (['processings', 'routes'].includes(kind)) {
        result.providers = await v4API.providers();
        if (!Array.isArray(result.providers)) throw new Error('Некорректный список провайдеров');
      }
      if (kind === 'routes' && current) {
        await Promise.all(['processings', 'tariffs', 'merchants'].map(async referenceKind => {
          const page = await loadConfigurationReferences(referenceKind, () => current);
          result[referenceKind] = page.items;
          if (page.truncated) notices.push(`${TITLES[referenceKind]}: показаны первые ${REFERENCE_CAP} записей или сервер повторил курсор. Список неполный; существующие выбранные ID сохранены.`);
        }));
      }
      if (['processings', 'merchants'].includes(kind)) {
        const response = await v4API.secrets();
        if (!Array.isArray(response?.items)) throw new Error('Не удалось загрузить список секретов');
        if (current) setSecrets(response.items.map(secretMetadata));
      }
      if (current) { setReferences(result); setWarnings(notices); }
    };
    load().catch(error => { if (current) setReferenceError(configurationError(error)); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [kind, retry]);

  useEffect(() => {
    if (kind !== 'merchants' || original) return undefined;
    let current = true;
    setLegacyLoading(true);
    setLegacyError('');
    const timeout = setTimeout(async () => {
      try {
        const result = await v4API.legacyMerchants({ search: legacySearch, limit: 100 });
        const items = Array.isArray(result) ? result : result?.items;
        if (!Array.isArray(items)) throw new Error('Некорректный список мерчантов');
        if (current) {
          setLegacyItems(items);
          setLegacyTruncated(items.length >= 100 || Boolean(result?.nextCursor));
        }
      } catch (error) { if (current) setLegacyError(configurationError(error)); }
      finally { if (current) setLegacyLoading(false); }
    }, 300);
    return () => { current = false; clearTimeout(timeout); };
  }, [kind, original, legacySearch, retry]);

  const change = (key, value) => {
    setForm(previous => {
      const next = { ...previous, [key]: value };
      if (key === 'tariffId') {
        const tariff = references.tariffs.find(item => item._id === value);
        if (tariff) { next.currency = tariff.currency; next.scale = String(tariff.scale); }
      }
      if (key === 'processingId' && value !== previous.processingId) next.method = '';
      if (key === 'provider' && ['btc', 'ltc'].includes(value)) {
        const host = value === 'btc' ? 'mempool.space' : 'litecoinspace.org';
        next.baseUrl = `https://${host}`; next.allowedHosts = value === 'btc' ? 'mempool.space\nblockstream.info' : host;
      }
      if (key === 'provider' && value === '911pay' && !original && !previous.baseUrl && !previous.allowedHosts) {
        next.baseUrl = 'https://911pay.cc'; next.allowedHosts = '911pay.cc';
      }
      return next;
    });
    setErrors(previous => {
      const next = { ...previous };
      delete next[key];
      return next;
    });
  };
  const processing = references.processings.find(item => item._id === form.processingId);
  const provider = kind === 'processings' ? form.provider : processing?.provider;
  const capabilities = references.providers.find(item => item.provider === provider)?.capabilities;
  const disabled = saving || loading;
  const helper = key => {
    if (key.endsWith('Ref')) return 'Выберите секрет из хранилища. Пустое необязательное поле не удаляет сохранённую ссылку.';
    if (key.includes('Hosts')) return 'Доменные имена без https://, через запятую или новую строку.';
    if (key === 'maxAmount') return 'Необязательно: пустое значение означает отсутствие верхней границы.';
    if (key === 'callbackUrl') return 'Публичный HTTPS URL. Пустое значение отключает обратный вызов, если это разрешено сервером.';
    if (key.endsWith('Units')) return `Сумма в ${form.currency || 'валюте тарифа'}, не в копейках. Точность: ${form.scale}.`;
    if (key === 'processingCommissionBps') return 'От 0 до 100 %, до двух знаков. Группирует долг, не уменьшает сумму долга и не меняет наценку клиенту. Пусто — отдельная группа «Ставка не задана».';
    if (['markupBps', 'providerFeeBps'].includes(key)) return 'Проценты: 1,25 = 1,25 %. До двух знаков после запятой, без округления.';
    if (key === 'legacyMultiplier') return 'Необязательно: положительное десятичное число, до 8 знаков после запятой.';
    return undefined;
  };
  const field = (key, options = {}) => <TextField
    key={key} id={`configuration-${key}`} fullWidth size="small" label={LABELS[key]} value={form[key] ?? ''}
    onChange={event => change(key, event.target.value)} disabled={disabled || options.disabled}
    error={Boolean(errors[key])} helperText={errors[key] || options.helperText || helper(key)}
    multiline={options.multiline} minRows={options.minRows} select={Boolean(options.choices)}
  >{options.choices?.map(choice => <MenuItem key={choice.value} value={choice.value}>{choice.label}</MenuItem>)}</TextField>;
  const choices = (key, values) => field(key, { choices: values.map(value => typeof value === 'string' ? { value, label: value } : value) });
  const lookup = (key, items, getId, getLabel, multiple = false) => {
    const selected = multiple ? form[key] : (form[key] ? [form[key]] : []);
    const options = [...items];
    selected.forEach(id => { if (!options.some(item => getId(item) === id)) options.push({ _fallbackId: id }); });
    const idOf = item => item._fallbackId || getId(item);
    const labelOf = item => item._fallbackId ? `${item._fallbackId} (сохранённый ID, вне списка)` : getLabel(item);
    return <Autocomplete key={key} multiple={multiple} options={options} disabled={disabled}
      value={multiple ? selected.map(id => options.find(item => idOf(item) === id)) : options.find(item => idOf(item) === form[key]) || null}
      getOptionLabel={labelOf} isOptionEqualToValue={(a, b) => idOf(a) === idOf(b)}
      onChange={(_, value) => change(key, multiple ? value.map(idOf) : value ? idOf(value) : '')}
      noOptionsText="Нет доступных записей" clearText="Очистить" openText="Открыть" closeText="Закрыть"
      renderInput={params => <TextField {...params} label={LABELS[key]} size="small" error={Boolean(errors[key])} helperText={errors[key] || 'Выберите вариант из списка: введённый текст без выбора не сохраняется.'} />}
    />;
  };
  const secretPicker = key => {
    const purpose = SECRET_PURPOSES[key];
    const options = secrets.filter(item => item.purpose === purpose && item.configured);
    if (form[key] && !options.some(item => item.ref === form[key])) options.push({ ref: form[key], label: 'Сохранённый секрет', purpose, source: 'environment', configured: true });
    const selected = options.find(item => item.ref === form[key]) || null;
    return <Stack key={key} spacing={1}>
      <Autocomplete options={options} value={selected} disabled={disabled}
        getOptionLabel={item => `${item.label || 'Без названия'} — ${secretSource(item.source)}`}
        isOptionEqualToValue={(a, b) => a.ref === b.ref}
        onChange={(_, item) => change(key, item?.ref || '')}
        noOptionsText="Нет секретов этого назначения. Добавьте новый." clearText="Очистить" openText="Открыть" closeText="Закрыть"
        renderInput={params => <TextField {...params} label={LABELS[key]} size="small" error={Boolean(errors[key])} helperText={errors[key] ? 'Выберите секрет подходящего назначения.' : helper(key)} />}
      />
      {selected?.source === 'environment' && <Alert severity="warning">Используется прежний секрет из окружения сервера. Сохранённая ссылка остаётся без изменений. Для переноса выполните явный импорт в разделе «Система».</Alert>}
      <Button sx={{ alignSelf: 'flex-start' }} disabled={disabled || system?.settingsReady === false || (key === 'credentialRef' && !provider)} onClick={() => setSecretField(key)}>Добавить: {LABELS[key]}</Button>
      {system?.settingsReady === false && <Typography variant="caption">Сначала подготовьте хранилище настроек в разделе «Система».</Typography>}
    </Stack>;
  };
  const confirmTelegramAccess = data => {
    if (kind === 'merchants' && form.settlementMode === 'telegram' && (original?.settlementMode !== 'telegram' || (form.telegramUsersEnabled === true && original?.telegramUsersEnabled !== true))) {
      setTelegramConfirmation(data);
      return;
    }
    onSave(data);
  };
  const submit = event => {
    event.preventDefault();
    if (saving || loading || referenceError) return;
    const result = buildConfiguration(kind, form, original);
    if (kind === 'routes' && capabilities) {
      if (!capabilities.currencies?.includes(form.currency)) result.errors.currency = 'Валюта не поддерживается выбранным провайдером';
    }
    setErrors(result.errors);
    if ((ADVANCED_FIELDS[kind] || []).some(key => result.errors[key])) setAdvancedOpen(true);
    if (Object.keys(result.errors).length) return;
    if (kind === 'merchants' && form.settlementMode === 'legacy' && original?.settlementMode !== 'legacy') {
      setLegacyConfirmation(result.data);
      return;
    }
    confirmTelegramAccess(result.data);
  };

  return <>
    <Dialog open fullWidth maxWidth="md" onClose={saving ? undefined : onClose} aria-labelledby="configuration-editor-title">
      <Box component="form" onSubmit={submit} noValidate>
        <DialogTitle id="configuration-editor-title">{original ? 'Редактирование' : 'Создание'}: {TITLES[kind]}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="info">{original ? `Ревизия: ${original.revision}. Статус меняется отдельно от сохранения формы.` : 'Новая запись создаётся отключённой. После проверки включите её отдельным действием в списке.'}</Alert>
            {saveError && <Alert severity="error">{saveError}</Alert>}
            {loading && <Stack direction="row" spacing={1}><CircularProgress size={20} /><Typography>Загрузка справочников…</Typography></Stack>}
            {referenceError && <Alert severity="error" action={<Button onClick={() => setRetry(value => value + 1)}>Повторить</Button>}>{referenceError}</Alert>}
            {warnings.map(warning => <Alert key={warning} severity="warning">{warning}</Alert>)}
            {Object.keys(errors).length > 0 && <Alert severity="error" aria-label="Ошибки формы">
              <Typography variant="body2">Исправьте следующие поля:</Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>{Object.entries(errors).map(([key, message]) => <li key={key}>{LABELS[key] || key}: {message}</li>)}</Box>
            </Alert>}
            {kind === 'processings' && <>
              {original && <Alert severity="info">Настройки можно менять и у включённого процессинга. Новые заказы используют обновлённые параметры; уже созданные — сохранённые настройки. Смена подключения сохраняет ID процессинга и его учёт долгов.</Alert>}
              {field('name')}
              {lookup('provider', references.providers, item => item.provider, item => item.provider)}
              <Capabilities capabilities={capabilities} />
              {['btc', 'ltc'].includes(provider) && <Alert severity="info">Оплата на свои адреса. API-ключ и webhook не нужны. Для BTC используется mempool.space с резервом blockstream.info. Добавьте свежие адреса в разделе «Криптоадреса». Для рублёвых заказов курс CoinGecko фиксируется при выдаче адреса; подтверждение — минимум один блок.</Alert>}
              {provider === '911pay' && <Alert severity="info">911pay H2H: базовый URL https://911pay.cc без /api. В учётных данных нужны Access Token и UUID мерчанта 911pay. Callback укажите на webhook этого процессинга V4; отдельный секрет webhook не требуется — статус проверяется авторизованным GET.</Alert>}
              {['baseUrl', 'allowedHosts', 'credentialRef', 'webhookSecretRef', 'callbackUrl'].map(key => SECRET_PURPOSES[key] ? secretPicker(key) : field(key, { multiline: key === 'allowedHosts' }))}
              {original?._id && system?.publicBaseUrl && <Button disabled={disabled} onClick={() => change('callbackUrl', `${system.publicBaseUrl.replace(/\/+$/, '')}/webhooks/${encodeURIComponent(original._id)}`)}>Подставить URL webhook V4</Button>}
            </>}
            {kind === 'merchants' && <>
              {!original && <>
                <Autocomplete freeSolo options={legacyItems} loading={legacyLoading}
                  getOptionLabel={item => typeof item === 'string' ? item : `${item.name || item.title || 'Мерчант'} — ${item._id || item.id || item.merchantId}`}
                  onInputChange={(_, value, reason) => { if (reason === 'input' || reason === 'clear') setLegacySearch(value); }}
                  onChange={(_, item) => { if (item && typeof item !== 'string') change('merchantId', String(item._id || item.id || item.merchantId)); }}
                  disabled={disabled} noOptionsText="Уточните поиск или введите ID ниже"
                  renderInput={params => <TextField {...params} size="small" label="Поиск существующего мерчанта" helperText="Найдите мерчанта по имени или введите его ID в поле ниже." />}
                />
                {legacyError && <Alert severity="error" action={<Button onClick={() => setRetry(value => value + 1)}>Повторить</Button>}>{legacyError}</Alert>}
                {legacyTruncated && <Alert severity="warning">Показаны первые 100 мерчантов. Уточните поиск или укажите известный ID вручную.</Alert>}
              </>}
              {field('merchantId', { disabled: Boolean(original), helperText: original ? 'ID существующего мерчанта неизменяем.' : 'ID записи существующего мерчанта, не ID конфигурации V4.' })}
              {choices('callbackVersion', ['v4', 'legacy'])}
              <Typography variant="caption" color="text.secondary">legacy — прежний формат apikey + order; v4 — события с идентификатором, версией и подписью.</Typography>
              {choices('settlementMode', [{ value: 'v4', label: 'v4 — кошелёк отдельного мерчанта' }, { value: 'telegram', label: 'telegram — Telegram-баланс V4' }, { value: 'legacy', label: 'legacy — старый баланс основной системы' }])}
              <Typography variant="caption" color="text.secondary">v4 — изолированные кошельки по мерчантам; telegram — единый для мерчантов Telegram-баланс V4, изолированный от legacy; legacy — реальные старые пользователи и балансы. Депозиты зачисляются Telegram-пользователям V4 только в режиме telegram. Снимки ранее созданных заказов не меняются.</Typography>
              {form.settlementMode === 'legacy' && <Alert severity="warning">Legacy-расчёты изменяют финансовую политику. При переходе потребуется отдельное подтверждение.</Alert>}
              <FormControlLabel label={LABELS.telegramUsersEnabled} control={<Switch checked={form.telegramUsersEnabled === true} disabled={disabled || (form.settlementMode !== 'telegram' && form.telegramUsersEnabled !== true)} onChange={event => change('telegramUsersEnabled', event.target.checked)} />} />
              <Alert severity="warning">Доступ разрешён только в режиме telegram: мерчант сможет списывать Telegram-баланс V4, общий для мерчантов, но изолированный от legacy. Старые балансы не меняются и автоматически не переносятся. Режим и доступ не переключаются автоматически.</Alert>
              {form.telegramUsersEnabled === true && form.settlementMode !== 'telegram' && <Alert severity="warning">Сохранён устаревший флаг доступа. Для сохранения явно отключите его или выберите режим telegram. Профиль не будет активирован автоматически.</Alert>}
              {errors.telegramUsersEnabled && <Alert severity="error">{errors.telegramUsersEnabled}</Alert>}
              <FormControlLabel label={LABELS.siteBalancesEnabled} control={<Switch checked={form.siteBalancesEnabled === true} disabled={disabled} onChange={event => change('siteBalancesEnabled', event.target.checked)} />} />
              <Typography variant="caption" color="text.secondary">Отдельный баланс в RUB для каждого логина у этого мерчанта. Сайт сможет получать остаток и списывать деньги на покупки. Доступ можно включить вместе с Telegram-балансами.</Typography>
              {['callbackUrl', 'callbackSecretRef', 'apiKeyRef', 'callbackAllowedHosts', 'receiptAllowedHosts', 'maxActiveOrders'].map(key => SECRET_PURPOSES[key] ? secretPicker(key) : field(key, { multiline: key.includes('Hosts') }))}
              <FormControlLabel label={LABELS.allowDepositFallback} control={<Switch checked={Boolean(form.allowDepositFallback)} disabled={disabled} onChange={event => change('allowDepositFallback', event.target.checked)} />} />
            </>}
            {kind === 'routes' && <>
              {lookup('processingId', references.processings, item => item._id, item => `${item.name} — ${item.provider}${item.enabled ? '' : ' (отключён)'}`)}
              <Capabilities capabilities={capabilities} />
              <Alert severity="info">Метод маршрута — техническая настройка провайдера. Введите его код вручную. Подбор выполняется по группе и ограничениям маршрута; метод не участвует в подборе.</Alert>
              {provider === 'primepay' && <Alert severity="info">PrimePay получает введённое значение в payment_method без подмены: для СБП укажите sbp. Мерчант получает method: card. Валюта — RUB, параметры провайдера — пустой JSON-объект: {'{}'}.</Alert>}
              {provider === '911pay' && <Alert severity="info">911pay: card — карта, sbp — телефон, account_number — банковский счёт. Валюта RUB, сумма для провайдера — целые рубли; настройте округление тарифа. PDF-чеки — до 2 МиБ. Видео — до 50 МиБ через того же Telegram-бота, что у SavagePay: задайте его videoBotTokenRef и ID группы 911pay в videoChatId.</Alert>}
              {lookup('tariffId', references.tariffs, item => item._id, item => `${item.name} — ${item.currency}, ${item.scale}${item.enabled ? '' : ' (отключён)'}`)}
              {field('processingCommissionBps')}
              {lookup('merchantIds', references.merchants, item => item.merchantId, item => `${item.merchantName || item.merchantId}${item.enabled ? '' : ' (отключён)'}`, true)}
              {field('method', { helperText: 'Код метода в протоколе провайдера. Не влияет на выбор маршрута.', inputProps: { maxLength: 32 } })}
              {field('currency', { disabled: true, helperText: 'Валюта и точность берутся из выбранного тарифа.' })}
              {field('scale', { disabled: true })}
              {['group', 'priority', 'position', 'minAmount', 'maxAmount'].map(key => field(key))}
              <Accordion expanded={advancedOpen} onChange={(_, expanded) => setAdvancedOpen(expanded)}>
                <AccordionSummary>Дополнительные настройки маршрута</AccordionSummary>
                <AccordionDetails><Stack spacing={2}>
                  {field('legacyKey')}{field('legacyMethod')}
                  <Alert severity="info">Параметры зависят от провайдера и проверяются сервером. Указывайте только поддерживаемые опции, без секретов. Для payscrow доступны uniqueAmount (логическое значение) и maxAdjustmentUnits (строка минимальных единиц). Для других провайдеров набор отличается; пустой объект допустим не всегда.</Alert>
                  {provider === '911pay' && <Alert severity="info">Опции 911pay: paymentGateway, payerBank, selfBank, isFloatingAmount, isTransgran, maxAdjustmentUnits. selfBank требует paymentGateway; isFloatingAmount — положительный maxAdjustmentUnits в минимальных единицах. Без paymentGateway передаётся currency: rub. Для видео переиспользуйте сохранённую ссылку бота SavagePay, не его токен; videoChatId задаётся отдельно. С одним videoBotTokenRef работает /chatid, но отправка видео ещё отключена. Изменения относятся только к новым заказам.</Alert>}
                  {field('providerOptions', { multiline: true, minRows: 5 })}
                </Stack></AccordionDetails>
              </Accordion>
            </>}
            {kind === 'tariffs' && <>
              {['name', 'currency', 'scale', 'markupBps', 'fixedUnits', 'providerFeeBps', 'providerFixedUnits', 'adjustmentUnits'].map(key => field(key))}
              {choices('rounding', [{ value: 'half_up', label: 'До ближайшего, половина вверх' }, { value: 'up', label: 'Вверх' }, { value: 'down', label: 'Вниз' }])}
              {original && <Alert severity="warning">При изменении точности суммы будут пересчитаны из показанных денежных значений. Проверьте связанные маршруты перед включением.</Alert>}
              <Accordion expanded={advancedOpen} onChange={(_, expanded) => setAdvancedOpen(expanded)}>
                <AccordionSummary>Дополнительные настройки тарифа</AccordionSummary>
                <AccordionDetails><Stack spacing={2}>
                  {ADVANCED_FIELDS.tariffs.map(key => field(key))}
                </Stack></AccordionDetails>
              </Accordion>
            </>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>Отмена</Button>
          <Button type="submit" variant="contained" disabled={disabled || Boolean(referenceError)}>{saving ? 'Сохранение…' : 'Сохранить'}</Button>
        </DialogActions>
      </Box>
    </Dialog>
    {secretField && <SecretEditor purpose={SECRET_PURPOSES[secretField]} provider={provider} onClose={() => setSecretField(null)} onCreated={item => {
      setSecrets(previous => [...previous.filter(secret => secret.ref !== item.ref), item]);
      change(secretField, item.ref);
      setSecretField(null);
    }} />}
    <Dialog open={Boolean(legacyConfirmation)} onClose={saving ? undefined : () => setLegacyConfirmation(null)} aria-labelledby="legacy-confirm-title">
      <DialogTitle id="legacy-confirm-title">Подтвердить legacy-расчёты?</DialogTitle>
      <DialogContent><Alert severity="warning">Вы переводите мерчанта на legacy-политику расчётов. Убедитесь, что это согласовано и совместимо с учётом средств. Сохранение не включает отключённую запись.</Alert></DialogContent>
      <DialogActions><Button disabled={saving} onClick={() => setLegacyConfirmation(null)}>Отмена</Button><Button color="warning" disabled={saving} onClick={() => { const data = legacyConfirmation; setLegacyConfirmation(null); confirmTelegramAccess(data); }}>Подтверждаю legacy-расчёты</Button></DialogActions>
    </Dialog>
    <Dialog open={Boolean(telegramConfirmation)} onClose={saving ? undefined : () => setTelegramConfirmation(null)} aria-labelledby="telegram-access-confirm-title">
      <DialogTitle id="telegram-access-confirm-title">Подтвердить Telegram-расчёты V4?</DialogTitle>
      <DialogContent><Stack spacing={2}>
        <Typography>Мерчант: {form.merchantId}</Typography>
        <Alert severity="warning">Новые расчёты используют Telegram-баланс V4, общий для мерчантов и изолированный от legacy. Старые балансы и снимки заказов не меняются. Сохранение не активирует отключённый профиль.</Alert>
        <Typography>Доступ к списаниям: {form.telegramUsersEnabled ? 'включён' : 'отключён'}</Typography>
      </Stack></DialogContent>
      <DialogActions><Button disabled={saving} onClick={() => setTelegramConfirmation(null)}>Отмена</Button><Button color="warning" disabled={saving} onClick={() => { const data = telegramConfirmation; setTelegramConfirmation(null); onSave(data); }}>Подтверждаю Telegram-расчёты V4</Button></DialogActions>
    </Dialog>
  </>;
}

function ReadyConfiguration({ kind }) {
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(undefined);
  const [history, setHistory] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [reload, setReload] = useState(0);
  const [editor, setEditor] = useState(null);
  const [activation, setActivation] = useState(null);
  const [deletion, setDeletion] = useState(null);
  const [deleteConflict, setDeleteConflict] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [keyBusy, setKeyBusy] = useState('');
  const [issuedKey, setIssuedKey] = useState(null);
  const requestId = useRef(0);
  const alive = useRef(false);
  const mutation = useRef(false);

  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  useEffect(() => {
    const id = ++requestId.current;
    setLoading(true);
    setError('');
    v4API.configList(kind, { cursor, limit: PAGE_SIZE }).then(page => {
      if (id !== requestId.current) return;
      if (!Array.isArray(page?.items)) throw new Error('Некорректный ответ списка конфигурации');
      setItems(page.items);
      setNextCursor(page.nextCursor || null);
    }).catch(reason => { if (id === requestId.current) { setError(configurationError(reason)); setItems([]); setNextCursor(null); } })
      .finally(() => { if (id === requestId.current) setLoading(false); });
    return () => { requestId.current += 1; };
  }, [kind, cursor, reload]);

  const persist = useCallback(async (original, data) => {
    if (mutation.current) return;
    mutation.current = true;
    setSaving(true);
    setSaveError('');
    setNotice('');
    try {
      if (original) await v4API.configUpdate(kind, original._id, data, original.revision);
      else {
        const created = await v4API.configCreate(kind, { ...data, enabled: false });
        if (kind === 'merchants' && created?.merchantId) {
          try {
            const key = await v4API.merchantApiKey(created.merchantId);
            if (key?.apiKey && alive.current) setIssuedKey(key);
          } catch { /* Ключ можно создать кнопкой в списке после обновления. */ }
        }
      }
      if (!alive.current) return;
      setEditor(null);
      setActivation(null);
      setNotice(original ? 'Изменения сохранены.' : 'Запись создана отключённой. Включение выполняется отдельно после проверки.');
      setReload(value => value + 1);
    } catch (reason) { if (alive.current) setSaveError(configurationError(reason)); }
    finally {
      mutation.current = false;
      if (alive.current) setSaving(false);
    }
  }, [kind]);

  async function removeConfiguration() {
    if (mutation.current || !deletion || deleteConflict) return;
    mutation.current = true;
    setSaving(true); setSaveError(''); setNotice('');
    try {
      await (kind === 'processings' ? v4API.deleteProcessing : v4API.deleteRoute)(deletion._id, deletion.revision);
      if (!alive.current) return;
      setDeletion(null);
      setNotice(kind === 'processings' ? 'Процессинг удалён.' : 'Маршрут удалён.');
      setCursor(null); setHistory([]);
      setReload(value => value + 1);
    } catch (reason) {
      if (alive.current) {
        setSaveError(configurationError(reason));
        if (reason?.response?.status === 409 && reason?.response?.data?.error?.code !== 'PROCESSING_HAS_ROUTES') setDeleteConflict(true);
      }
    } finally {
      mutation.current = false;
      if (alive.current) setSaving(false);
    }
  }

  async function ensureKey(item) {
    if (keyBusy) return;
    setKeyBusy(item._id); setError(''); setNotice('');
    try {
      const result = await v4API.merchantApiKey(item.merchantId);
      if (!alive.current) return;
      setItems(previous => previous.map(row => row._id === item._id ? { ...row, apiKey: result.apiKey } : row));
      if (result.generated) setIssuedKey(result);
    } catch (reason) { if (alive.current) setError(configurationError(reason)); }
    finally { if (alive.current) setKeyBusy(''); }
  }
  async function copyKey(value) {
    try { await navigator.clipboard.writeText(value); setNotice('API-ключ скопирован.'); }
    catch { setError('Не удалось скопировать ключ. Выделите текст вручную.'); }
  }

  return <Stack spacing={2}>
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
      <Button variant="contained" disabled={saving || loading} onClick={() => { setSaveError(''); setEditor({ original: null }); }}>Создать</Button>
      <Button disabled={saving || loading} onClick={() => setReload(value => value + 1)}>Обновить</Button>
    </Stack>
    <Typography variant="body2" color="text.secondary">Изменения сохраняются с проверкой ревизии. {['routes', 'processings'].includes(kind)
      ? 'Удаление доступно с подтверждением; история заказов сохраняется.' : 'Ненужную запись можно отключить.'}</Typography>
    {notice && <Alert severity="success" onClose={() => setNotice('')}>{notice}</Alert>}
    {issuedKey && <Alert severity="success" onClose={() => setIssuedKey(null)}>
      <Typography variant="body2">Новый API-ключ мерчанта создан и сохранён{issuedKey.generated ? ' автоматически' : ''}:</Typography>
      <Typography component="code" sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere', display: 'block' }}>{issuedKey.apiKey}</Typography>
      <Typography variant="caption">Передайте ключ мерчанту — он подходит и для старого API, и для V4.</Typography>
    </Alert>}
    {error && <Alert severity="error" action={<Button onClick={() => setReload(value => value + 1)}>Повторить</Button>}>{error}</Alert>}
    {loading ? <Stack direction="row" spacing={1}><CircularProgress size={24} /><Typography>Загрузка конфигурации…</Typography></Stack> : !error && <>
      {!items.length ? <Alert severity="info">Записей пока нет.</Alert> : <TableContainer>
        <Table size="small" aria-label={TITLES[kind]}>
          <TableHead><TableRow><TableCell>Название / ID</TableCell>{kind === 'routes' && <><TableCell>Процессинг</TableCell><TableCell>Комиссия процессинга</TableCell></>}<TableCell>Параметры</TableCell><TableCell>Статус</TableCell><TableCell>Ревизия</TableCell><TableCell>Действия</TableCell></TableRow></TableHead>
          <TableBody>{items.map(item => <TableRow key={item._id}>
            <TableCell><Typography variant="body2">{item.name || item.merchantName || item.merchantId || item.method || item._id}</Typography><Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>{item._id}</Typography></TableCell>
            {kind === 'routes' && <TableCell>
              <Typography variant="body2">{item.processingName || 'Процессинг не найден'}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>{item.processingId}</Typography>
            </TableCell>}
            {kind === 'routes' && <TableCell>{item.processingCommissionBps == null
              ? 'Не задана' : `${(item.processingCommissionBps / 100).toLocaleString('ru-RU', { maximumFractionDigits: 2 })} %`}</TableCell>}
            <TableCell>
              {kind === 'processings' ? item.provider : kind === 'merchants' ? `Расчёты: ${item.settlementMode}; callback: ${item.callbackVersion}` : `${item.currency}, точность ${item.scale}`}
              {kind === 'merchants' && <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                <Typography variant="caption">Telegram-баланс V4: {item.telegramUsersEnabled === true ? (item.settlementMode === 'telegram' ? 'доступ разрешён' : 'устаревший флаг — требуется исправление') : 'доступ отключён'}</Typography>
                <Typography variant="caption" display="block">Балансы сайта: {item.siteBalancesEnabled === true ? 'доступ разрешён' : 'доступ отключён'}</Typography>
                {item.apiKey
                  ? <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}>API-ключ: {item.apiKey}</Typography>
                      <Button size="small" onClick={() => copyKey(item.apiKey)}>Копировать</Button>
                    </Stack>
                  : <Button size="small" disabled={Boolean(keyBusy)} onClick={() => ensureKey(item)}>{keyBusy === item._id ? 'Создание…' : 'Создать API-ключ'}</Button>}
              </Stack>}
            </TableCell>
            <TableCell><Chip size="small" color={item.enabled ? 'success' : 'default'} label={item.enabled ? 'Включено' : 'Отключено'} /></TableCell>
            <TableCell>{item.revision}</TableCell>
            <TableCell><Stack direction="row" spacing={1}>
              <Button disabled={saving} onClick={() => { setSaveError(''); setEditor({ original: item }); }}>Редактировать</Button>
              <Button disabled={saving} color={item.enabled ? 'warning' : 'primary'} onClick={() => { setSaveError(''); setActivation(item); }}>{item.enabled ? 'Отключить' : 'Включить'}</Button>
              {['routes', 'processings'].includes(kind) && <Button color="error" disabled={saving} onClick={() => {
                setSaveError(''); setDeleteConflict(false); setDeletion(item);
              }}>Удалить</Button>}
            </Stack></TableCell>
          </TableRow>)}</TableBody>
        </Table>
      </TableContainer>}
    </>}
    <Stack direction="row" alignItems="center" spacing={2}>
      <Button disabled={loading || saving || !history.length} onClick={() => { setCursor(history[history.length - 1]); setHistory(value => value.slice(0, -1)); }}>Назад</Button>
      <Typography variant="body2">Страница {history.length + 1}</Typography>
      <Button disabled={loading || saving || !nextCursor} onClick={() => { setHistory(value => [...value, cursor]); setCursor(nextCursor); }}>Далее</Button>
    </Stack>
    {editor && <ConfigurationEditor kind={kind} original={editor.original} saving={saving} saveError={saveError} onSave={data => persist(editor.original, data)} onClose={() => { if (!saving) setEditor(null); }} />}
    <Dialog open={Boolean(deletion)} onClose={saving ? undefined : () => setDeletion(null)} aria-labelledby="delete-route-title">
      <DialogTitle id="delete-route-title">{kind === 'processings' ? 'Удалить процессинг?' : 'Удалить маршрут?'}</DialogTitle>
      <DialogContent><Stack spacing={2}>
        <Typography sx={{ overflowWrap: 'anywhere' }}>ID: {deletion?._id}</Typography>
        {kind === 'processings' ? <>
          <Typography>{deletion?.name} · {deletion?.provider}</Typography>
          <Alert severity="warning">Процессинг исчезнет из списка и станет недоступен для новых маршрутов и заказов. Уже созданные заказы, колбеки, история и долги сохранятся. Сначала удалите или переназначьте все связанные маршруты, включая отключённые.</Alert>
        </> : <>
          <Typography>Группа: {deletion?.group || 'Без группы'} · Метод: {deletion?.method}</Typography>
          <Alert severity="warning">Маршрут исчезнет из настроек и перестанет выбираться для новых заказов. Уже созданные заказы продолжат обработку с прежними настройками; история сохранится.</Alert>
        </>}
        {saveError && <Alert severity="error">{saveError}</Alert>}
        {deleteConflict && <Alert severity="warning">{kind === 'processings' ? 'Процессинг изменился.' : 'Маршрут изменился.'} Обновите список и проверьте его перед удалением.</Alert>}
      </Stack></DialogContent>
      <DialogActions>
        <Button disabled={saving} onClick={() => setDeletion(null)}>Отмена</Button>
        {deleteConflict
          ? <Button disabled={saving} onClick={() => { setDeletion(null); setReload(value => value + 1); }}>Обновить список</Button>
          : <Button color="error" variant="contained" disabled={saving || !deletion} onClick={removeConfiguration}>{saving ? 'Удаление…' : kind === 'processings' ? 'Удалить процессинг' : 'Удалить маршрут'}</Button>}
      </DialogActions>
    </Dialog>
    <Dialog open={Boolean(activation)} onClose={saving ? undefined : () => setActivation(null)} aria-labelledby="activation-title">
      <DialogTitle id="activation-title">{activation?.enabled ? 'Отключить запись?' : 'Включить запись?'}</DialogTitle>
      <DialogContent><Stack spacing={2}>
        <Typography>{activation?.name || activation?.merchantId || activation?._id}</Typography>
        <Alert severity="warning">{activation?.enabled ? 'Запись перестанет использоваться для новых операций. История сохраняется.' : 'Запись станет доступна для реальных операций. Проверьте подключение, секреты, тарифы и зависимости. Сервер повторно проверит конфигурацию.'}</Alert>
        {kind === 'merchants' && activation && !activation.enabled && !activation.callbackUrl && <Alert severity="warning">У мерчанта нет адреса колбека. Уведомления отправляться не будут; получение статусов возможно только через API.</Alert>}
        {saveError && <Alert severity="error">{saveError}</Alert>}
      </Stack></DialogContent>
      <DialogActions><Button disabled={saving} onClick={() => setActivation(null)}>Отмена</Button><Button variant="contained" disabled={saving || !activation} onClick={() => persist(activation, { enabled: !activation.enabled })}>{saving ? 'Сохранение…' : activation?.enabled ? 'Подтвердить отключение' : 'Подтвердить включение'}</Button></DialogActions>
    </Dialog>
  </Stack>;
}

export default function Configuration({ kind }) {
  const { system } = useV4();
  if (!Object.prototype.hasOwnProperty.call(CONFIGURATION_FIELDS, kind)) return <Alert severity="error">Неизвестный раздел конфигурации.</Alert>;
  if (!system?.ready) return <Alert severity="warning">Конфигурация недоступна: V4 ещё не готова. Проверьте состояние системы, подключение базы данных и настройки V4, затем обновите состояние. До готовности чтение и изменение конфигурации отключены.</Alert>;
  return <ReadyConfiguration key={kind} kind={kind} />;
}
